/*
 * MDB shim for MDBBtn
 * Files currently importing this symbol from "mdbreact":
 * - src/Components/Custom/InformationChangeModal.jsx
 * - src/Components/Pipeline/MonthGlance.jsx
 * - src/Seating/Accounts/accountDetail.jsx
 * - src/Seating/Activities/activities-edit.jsx
 * - src/Seating/Deliveries/deliveryList.jsx
 * - src/Seating/Faxes/faxes.jsx
 * - src/Seating/Orders/ATPView.jsx
 * - src/Seating/Orders/RecurringOrders.jsx
 * - src/Seating/Orders/collectionsView.jsx
 * - src/Seating/Patient/PatientViewComponents/CreateQuoteModalFromTicket.jsx
 * - src/Seating/Patient/PatientViewComponents/HomeAssessmentModal.jsx
 * - src/Seating/Patient/PatientViewComponents/attachmentsTab.jsx
 * - src/Seating/Patient/PatientViewComponents/createNoteModal.jsx
 * - src/Seating/Patient/PatientViewComponents/liaisonModal.jsx
 * - src/Seating/Patient/PatientViewComponents/notesTab.jsx
 * - src/Seating/Patient/PatientViewComponents/patientInformationTab.jsx
 * - src/Seating/Patient/PatientViewComponents/typeStatusContainer.jsx
 * - src/Seating/Patient/patientInformationView.jsx
 * - src/Seating/Patient/patientOrderView.jsx
 * - src/Seating/Patient/pqQuoteSelector.jsx
 * - src/Seating/Settings/stateSalesTax.jsx
 * - src/Seating/Settings/trainingLink.jsx
 * - src/Seating/Users/userAdd.jsx
 * - src/Seating/Users/userEdit.jsx
 */

import MUIButton from "@mui/material/Button";

/*
 * MDB Shim Template Decisions
 *
 * Purpose:
 * - Provide drop-in MDB component APIs while rendering with MUI components.
 *
 * Rules for all shims:
 * - Prioritize functional compatibility over visual mimicry.
 * - Map MDB props that affect behavior, readability, visibility, and expected shape.
 * - Use MUI defaults for non-essential/cosmetic styling.
 * - Avoid adding style overrides unless required for compatibility.
 * - Keep implementation small and direct: one file per MDB component in MDBShim/.
 *
 * Concretely for MDBBtn:
 * - Keep prop translations like color, size, outline, block, tag, rounded/circle/floating.
 * - Do not force typography or shadow styles when MUI defaults are acceptable.
 */

const SIZE_MAP = {
    sm: "small",
    md: "medium",
    med: "medium",
    large: "large",
};

const MUI_COLOR_MAP = {
    primary: "primary",
    secondary: "secondary",
    success: "success",
    info: "info",
    warning: "warning",
    danger: "error",
    red: "error",
    blue: "primary",
    green: "success",
};

const CUSTOM_COLOR_MAP = {
    amber: "#ffc107",
    default: "#6c757d",
    elegant: "#2e2e2e",
    gray: "#6c757d",
    grey: "#6c757d",
    "light grey": "#d3d3d3",
    "mdb-color": "#59698d",
    orange: "#fb8c00",
    teal: "#009688",
    "teal darken-2": "#00796b",
    white: "#ffffff",
    whitesmoke: "#f5f5f5",
    yellow: "#ffeb3b",
};

function getBtnColorStyles(color, outline) {
    if (!color) {
        return {};
    }

    const normalizedColor = String(color).toLowerCase();
    const customColor = CUSTOM_COLOR_MAP[normalizedColor];
    if (!customColor) {
        return {};
    }

    if (outline) {
        return {
            color: customColor,
            borderColor: customColor,
        };
    }

    const textColor = normalizedColor === "white" || normalizedColor === "yellow" || normalizedColor === "light grey" || normalizedColor === "whitesmoke"
        ? "#212529"
        : "#ffffff";

    return {
        backgroundColor: customColor,
        color: textColor,
    };
}

function getShapeStyles({ circle, floating, rounded, size }) {
    if (!circle && !floating && !rounded) {
        return {};
    }

    const sizeMap = {
        small: 32,
        medium: 40,
        large: 48,
    };
    const diameter = sizeMap[size] || sizeMap.medium;

    if (circle || floating) {
        return {
            borderRadius: "50%",
            minWidth: diameter,
            width: diameter,
            height: diameter,
            padding: 0,
        };
    }

    if (rounded) {
        return {
            borderRadius: "999px",
        };
    }

    return {};
}

export default function MDBBtn(props) {
    const {
        color,
        size,
        outline,
        rounded,
        circle,
        floating,
        block,
        tag,
        sx,
        ...rest
    } = props;

    const muiSize = SIZE_MAP[size] || "medium";
    const normalizedColor = color ? String(color).toLowerCase() : "";
    const muiColor = MUI_COLOR_MAP[normalizedColor] || "primary";

    const shapeStyles = getShapeStyles({
        circle,
        floating,
        rounded,
        size: muiSize,
    });
    const colorStyles = getBtnColorStyles(color, outline);

    return (
        <MUIButton
            variant={outline ? "outlined" : "contained"}
            color={muiColor}
            size={muiSize}
            fullWidth={Boolean(block)}
            component={tag}
            sx={{
                ...shapeStyles,
                ...colorStyles,
                ...sx,
            }}
            {...rest}
        />
    );
}
